import React, { useEffect, useRef, useState } from "react";
import { matchPath, useLocation, useNavigate } from "react-router-dom";
import { FullScreenLoader } from "src/app/components";
import { usePreviousRoute } from "src/app/guards/PreviousRouteContext";
import BackButtonLayout from "src/app/layouts/BackButtonLayout";
import { AppRoutes } from "src/app/navigation/routes";
import { useAppSelector } from "src/app/services/store";
import { PostMessageEvents } from "src/shared/config/src/lib/constants";
import { getCurrentChild } from "src/shared/redux-state/src";
import { ChildrenStoreData } from "src/shared/types/src";

const getOrigin = (url: string) => {
  try {
    return new URL(url).origin;
  } catch (err) {
    console.error("Invalid URL:", url);
    return null;
  }
};

function buildParentURLForChildPath(childPath: string) {
  // childPath already includes leading slash and its own ?search#hash
  return `${AppRoutes.EYMEMORIES}${
    childPath.startsWith("/") ? "" : "/"
  }${childPath}`;
}

function getChildPathFromParentURL() {
  const { pathname, search, hash } = window.location;
  let rest = pathname.slice(AppRoutes.EYMEMORIES.length) || "/";
  if (!rest.startsWith("/")) rest = "/" + rest;
  return `${rest}${search ?? ""}${hash ?? ""}`;
}

type EymemoriesProps = {};

const Eymemories: React.FC<EymemoriesProps> = ({}) => {
  const location = useLocation();
  const navigate = useNavigate();
  const { previousRoute, canGoBackInApp } = usePreviousRoute();
  const [loader, setLoader] = useState<boolean>(true);
  const currentChild: ChildrenStoreData = useAppSelector(getCurrentChild);
  const [url, setUrl] = useState<string>(currentChild?.eyMemoriesUrl);
  const iframeRef = useRef<HTMLIFrameElement>(null);
  const eymemoryClientOrigin = useRef<string | null>(
    getOrigin(currentChild?.eyMemoriesUrl)
  );

  const handleUrl = () => {
    eymemoryClientOrigin.current = getOrigin(currentChild?.eyMemoriesUrl);
    if (location?.state?.redirectTo && eymemoryClientOrigin.current) {
      const { redirectTo } = location.state;
      setUrl(`${eymemoryClientOrigin.current}${redirectTo}`);
    } else {
      if (!currentChild?.eyMemoriesUrl) setUrl("");
      setUrl(currentChild?.eyMemoriesUrl);
    }
  };

  useEffect(() => {
    handleUrl();
    const handleMessageEvent = (event: MessageEvent) => {
      if (event.origin === eymemoryClientOrigin.current) {
        const { type, path, paymentUrl } = event.data || {};
        if (paymentUrl && type === PostMessageEvents.STRIPE_CHECKOUT) {
          window.location.assign(event?.data?.paymentUrl);
        }
        if (
          type === PostMessageEvents.CHILD_NAVIGATE &&
          typeof path === "string"
        ) {
          const target = buildParentURLForChildPath(path);

          // Avoid redundant pushes
          const current =
            window.location.pathname +
            window.location.search +
            window.location.hash;
          if (current === target) return;

          // Notify React Router (keeps hooks like useLocation in sync)
          navigate(target, { replace: true });
        }
      }
    };
    window.addEventListener("message", handleMessageEvent);

    const onPopState = () => {
      const childPath = getChildPathFromParentURL();
      iframeRef.current?.contentWindow?.postMessage(
        { type: PostMessageEvents.PARENT_NAVIGATE, path: childPath },
        eymemoryClientOrigin.current as string
      );
    };
    window.addEventListener("popstate", onPopState);

    return () => {
      window.removeEventListener("message", handleMessageEvent);
      window.removeEventListener("popstate", onPopState);
    };
  }, []);

  useEffect(() => {
    handleUrl();
  }, [currentChild?.eyMemoriesUrl]);

  const iFrameLoaded = () => {
    setLoader(false);
    if (url && iframeRef?.current?.contentWindow) {
      const childPath = location?.state?.redirectTo
        ? location?.state?.redirectTo
        : getChildPathFromParentURL();
      iframeRef.current.contentWindow.postMessage(
        {
          type: PostMessageEvents.INIT,
          parentUrl: window.location.origin,
          path: childPath,
        },
        new URL(url).origin
      );
    }
    if (location?.state?.redirectTo) {
      const current =
        window.location.pathname +
        window.location.search +
        window.location.hash;

      navigate(current, { replace: true, state: undefined });
    }
  };

  const calculateHeight = (value: number) => {
    return window.innerHeight - value;
  };

  const handleBackClick = () => {
    const isEymemoryRoute = matchPath(
      `${AppRoutes.EYMEMORIES}/*`,
      previousRoute as string
    );
    if (
      !canGoBackInApp &&
      (previousRoute === AppRoutes.EYMEMORIES || isEymemoryRoute)
    ) {
      navigate(AppRoutes.OPTIONS);
    } else if (isEymemoryRoute && location.pathname.includes("checkout")) {
      if (iframeRef?.current?.contentWindow) {
        iframeRef.current?.contentWindow?.postMessage(
          { type: PostMessageEvents.PARENT_NAVIGATE, path: "/" },
          eymemoryClientOrigin.current as string
        );
        navigate(AppRoutes.EYMEMORIES);
        return;
      }
      navigate(AppRoutes.OPTIONS);
    } else if (
      isEymemoryRoute &&
      [AppRoutes.EYMEMORIES, `${AppRoutes.EYMEMORIES}/`].includes(
        location.pathname
      )
    ) {
      navigate(AppRoutes.OPTIONS);
    } else {
      navigate(-1);
    }
  };

  return (
    <BackButtonLayout title="eymemories" onBackClick={handleBackClick}>
      <div
        className="position-relative"
        style={{ height: calculateHeight(100) }}
      >
        {url ? (
          <>
            <iframe
              title="EymemoriesIframe"
              className={"w-100 mg-t-20"}
              onLoad={iFrameLoaded}
              ref={iframeRef}
              src={url}
              style={{ height: calculateHeight(100) }}
            />
            {loader ? <FullScreenLoader /> : null}
          </>
        ) : (
          <div className="d-flex align-items-center justify-content-center h-100 fs-14 fw-semibold text-black">
            Failed to load iframe
          </div>
        )}
      </div>
    </BackButtonLayout>
  );
};

export default Eymemories;
